import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Download, FileSpreadsheet } from "lucide-react";
import {
  useStoreSalesReport,
  useInventoryValueReport,
  useCommissionsReport,
  useStockoutForecast,
  useBelowCostSalesReport,
  useSalesGoalsProgress,
  downloadReportCsv,
} from "@/api/reports";
import { PAYMENT_METHOD_LABEL } from "@/api/pos";
import { useBranches } from "@/api/inventory";
import { useCreateDraftPurchasesFromForecast, DraftPurchasesFromForecastResult } from "@/api/purchases";
import { getApiErrorMessage } from "@/api/client";

/** Botones "Exportar CSV/Excel" reutilizados en cada sección de este reporte — mismo patrón que ReportsPage.tsx (ver revenue-by-service). */
function ExportButtons({ endpoint, filename, params }: { endpoint: string; filename: string; params?: Record<string, string | number | undefined> }) {
  const [exporting, setExporting] = useState<"csv" | "xlsx" | null>(null);
  const handleExport = async (format: "csv" | "xlsx") => {
    setExporting(format);
    try {
      await downloadReportCsv(`${endpoint}.${format}`, `${filename}.${format}`, params);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setExporting(null);
    }
  };
  return (
    <div className="flex shrink-0 gap-2">
      <button type="button" className="btn-secondary text-xs" disabled={exporting !== null} onClick={() => handleExport("xlsx")}>
        <FileSpreadsheet size={13} className="mr-1" /> Excel
      </button>
      <button type="button" className="btn-secondary text-xs" disabled={exporting !== null} onClick={() => handleExport("csv")}>
        <Download size={13} className="mr-1" /> CSV
      </button>
    </div>
  );
}

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const today = isoDate(new Date());
const thirtyDaysAgo = isoDate(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000));

/**
 * Reportes de ventas/rentabilidad de la tienda (cuarta de las cuatro
 * mejoras "día a día"). La utilidad es una ESTIMACIÓN con el costo actual
 * del producto — no se guarda un costo histórico por línea de venta, así
 * que si el costo de un producto cambió durante el rango, la cifra de
 * utilidad de ventas antiguas no será exacta. Se avisa en pantalla.
 */
export default function StoreReportsPage() {
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [branchId, setBranchId] = useState("");
  const { data: branches } = useBranches();
  const { data, isLoading } = useStoreSalesReport({ dateFrom, dateTo, branchId: branchId || undefined });
  const { data: inventoryValue } = useInventoryValueReport();
  const { data: commissions } = useCommissionsReport({ dateFrom, dateTo });
  const { data: belowCostSales } = useBelowCostSalesReport({ dateFrom, dateTo, branchId: branchId || undefined });
  const { data: stockoutForecast, isLoading: isLoadingForecast } = useStockoutForecast(14);

  const [goalMonth, setGoalMonth] = useState(today.slice(0, 7)); // "YYYY-MM", por defecto el mes en curso
  const [goalYear, goalMonthNum] = goalMonth.split("-").map((v) => Number(v));
  const { data: salesGoals } = useSalesGoalsProgress({ year: goalYear, month: goalMonthNum });

  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [orderQuantities, setOrderQuantities] = useState<Record<string, string>>({});
  const [orderBranchId, setOrderBranchId] = useState("");
  const [forecastResult, setForecastResult] = useState<DraftPurchasesFromForecastResult | null>(null);
  const createDraftPurchases = useCreateDraftPurchasesFromForecast();

  useEffect(() => {
    if (!orderBranchId && branches && branches.length > 0) {
      setOrderBranchId(branches.find((b) => b.isMain)?.id ?? branches[0].id);
    }
  }, [branches, orderBranchId]);

  const toggleProductSelection = (productId: string, suggestedQty: number) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
        setOrderQuantities((q) => (q[productId] !== undefined ? q : { ...q, [productId]: String(suggestedQty) }));
      }
      return next;
    });
  };

  const toggleSelectAllForecast = () => {
    if (!stockoutForecast) return;
    if (selectedProductIds.size === stockoutForecast.length) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(stockoutForecast.map((item) => item.productId)));
      setOrderQuantities((q) => {
        const next = { ...q };
        for (const item of stockoutForecast) {
          if (next[item.productId] === undefined) next[item.productId] = String(item.suggestedOrderQty);
        }
        return next;
      });
    }
  };

  const handleGenerateDraftPurchases = async () => {
    if (!orderBranchId) {
      toast.error("Elige la sucursal que recibirá la mercancía.");
      return;
    }
    const items = Array.from(selectedProductIds)
      .map((productId) => ({ productId, quantity: orderQuantities[productId] ?? "0" }))
      .filter((item) => Number(item.quantity) > 0);
    if (items.length === 0) {
      toast.error("Selecciona al menos un producto con cantidad mayor a cero.");
      return;
    }
    try {
      const result = await createDraftPurchases.mutateAsync({ branchId: orderBranchId, items });
      setForecastResult(result);
      setSelectedProductIds(new Set());
      if (result.createdPurchases.length > 0) {
        toast.success(`${result.createdPurchases.length} orden(es) de compra generada(s) como borrador — revísalas en Compras.`);
      } else {
        toast.error("Ningún producto seleccionado tiene proveedor identificable en su historial de compras.");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const setPreset = (days: number) => {
    setDateFrom(isoDate(new Date(Date.now() - (days - 1) * 24 * 60 * 60 * 1000)));
    setDateTo(today);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reportes de ventas</h1>
          <p className="text-sm text-gray-500">Resumen de ventas, productos más vendidos y rentabilidad estimada de la tienda.</p>
        </div>
        <ExportButtons endpoint="/reports/store-sales" filename="ventas-tienda" params={{ dateFrom, dateTo, branchId: branchId || undefined }} />
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Desde</span>
          <input type="date" className="input-field w-auto" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <span>hasta</span>
          <input type="date" className="input-field w-auto" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={() => setPreset(7)}>
            7 días
          </button>
          <button type="button" className="btn-secondary" onClick={() => setPreset(30)}>
            30 días
          </button>
          <button type="button" className="btn-secondary" onClick={() => setPreset(90)}>
            90 días
          </button>
        </div>
        {branches && branches.length > 1 && (
          <select className="input-field w-auto" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">Todas las sucursales</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {isLoading && <div className="card p-6 text-sm text-gray-400">Cargando...</div>}

      {!isLoading && data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryCard label="Ventas" value={data.kpis.salesCount} isCount />
            <SummaryCard label="Ticket promedio" value={data.kpis.averageTicket} />
            <SummaryCard label="Total cobrado" value={data.kpis.total} tone="brand" />
            <SummaryCard label="IVA cobrado" value={data.kpis.taxTotal} />
            <SummaryCard label="Costo estimado" value={data.kpis.costEstimate} />
            <SummaryCard label="Utilidad estimada" value={data.kpis.profitEstimate} tone={data.kpis.profitEstimate >= 0 ? "green" : "red"} />
            <SummaryCard label="Margen estimado" value={data.kpis.marginPercent} suffix="%" />
            <SummaryCard label="Subtotal (sin IVA)" value={data.kpis.subtotal} />
          </div>
          <p className="text-xs text-gray-400">
            * El costo y la utilidad son una estimación con el costo actual de cada producto — no se guarda el costo histórico exacto al momento de cada venta.
          </p>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="card p-4 lg:col-span-2">
              <p className="mb-3 text-sm font-semibold text-gray-900">Ventas por día</p>
              <SalesByDayChart data={data.byDay} />
            </div>
            <div className="card p-4">
              <p className="mb-3 text-sm font-semibold text-gray-900">Por método de pago</p>
              <div className="space-y-2 text-sm">
                {data.byPaymentMethod.map((p) => (
                  <div key={p.paymentMethod} className="flex items-center justify-between">
                    <span className="text-gray-600">{PAYMENT_METHOD_LABEL[p.paymentMethod as keyof typeof PAYMENT_METHOD_LABEL] ?? p.paymentMethod}</span>
                    <span className="font-medium text-gray-900">{currency.format(p.total)}</span>
                  </div>
                ))}
                {data.byPaymentMethod.length === 0 && <p className="text-gray-400">Sin ventas en este rango.</p>}
              </div>
            </div>
          </div>

          {!branchId && data.byBranch.length > 1 && (
            <div className="card overflow-hidden">
              <p className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">Comparativo por sucursal</p>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Sucursal</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Ventas</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Total cobrado</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Ticket promedio</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.byBranch.map((b) => (
                    <tr key={b.branchId}>
                      <td className="px-4 py-2 text-gray-700">{b.branchName}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{b.count}</td>
                      <td className="px-4 py-2 text-right text-gray-900">{currency.format(b.total)}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{currency.format(b.averageTicket)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            <ProductTable title="Más vendidos por ingresos" rows={data.topProductsByRevenue} />
            <ProductTable title="Más rentables (utilidad estimada)" rows={data.topProductsByProfit} />
            <RotationTable title="Rotación (más vendidos por unidades)" rows={data.topProductsByQuantity} />
          </div>

          <div className="card overflow-hidden">
            <p className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">Ventas por categoría</p>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Categoría</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Unidades</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Ingresos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.byCategory.map((c) => (
                  <tr key={c.categoryId ?? "sin-categoria"}>
                    <td className="px-4 py-2 text-gray-700">{c.categoryName}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{c.quantitySold}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{currency.format(c.revenue)}</td>
                  </tr>
                ))}
                {data.byCategory.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-gray-400">
                      Sin ventas en este rango.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {commissions && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3 pt-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Comisiones por vendedor</h2>
              <p className="text-sm text-gray-500">
                Sobre el total de cada venta completada en este rango. El % de cada vendedor se configura en Usuarios.
              </p>
            </div>
            <ExportButtons endpoint="/reports/store-commissions" filename="comisiones" params={{ dateFrom, dateTo }} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SummaryCard label="Comisión total del periodo" value={commissions.totalCommission} tone="brand" />
            <SummaryCard label="Vendedores con ventas" value={commissions.rows.length} isCount />
          </div>
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Vendedor</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Ventas</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Total vendido</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">% comisión</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Comisión</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {commissions.rows.map((r) => (
                  <tr key={r.userId}>
                    <td className="px-4 py-2 text-gray-900">{r.userName}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{r.salesCount}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{currency.format(r.salesTotal)}</td>
                    <td className={`px-4 py-2 text-right ${r.commissionRate === 0 ? "text-gray-400" : "text-gray-600"}`}>{r.commissionRate}%</td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900">{currency.format(r.commissionAmount)}</td>
                  </tr>
                ))}
                {commissions.rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                      Sin ventas con vendedor asignado en este rango.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="pt-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Metas de venta</h2>
            <p className="text-sm text-gray-500">
              Meta mensual de cada vendedor (se configura en Usuarios) contra sus ventas completadas del mes seleccionado.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <input
              type="month"
              className="input-field w-40"
              value={goalMonth}
              onChange={(e) => setGoalMonth(e.target.value)}
            />
            <ExportButtons endpoint="/reports/store-sales-goals-progress" filename={`metas-de-venta-${goalMonth}`} params={{ year: goalYear, month: goalMonthNum }} />
          </div>
        </div>
      </div>
      {salesGoals && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <SummaryCard label="Meta total del mes" value={salesGoals.totalGoal} tone="brand" />
            <SummaryCard label="Vendido en el mes" value={salesGoals.totalSales} />
            <SummaryCard label="% de la meta alcanzado" value={salesGoals.totalProgressPercent} suffix="%" tone={salesGoals.totalProgressPercent >= 100 ? "green" : "gray"} />
          </div>
          <div className="card overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Vendedor</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Meta</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Vendido</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">% cumplido</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Falta</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500" title="Si el mes sigue al ritmo actual de ventas, hacia dónde va a cerrar">
                    Proyección de cierre
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {salesGoals.rows.map((r) => (
                  <tr key={r.userId}>
                    <td className="px-4 py-2 text-gray-900">{r.userName}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{currency.format(r.monthlyGoal)}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{currency.format(r.salesTotal)}</td>
                    <td className={`px-4 py-2 text-right font-medium ${r.progressPercent >= 100 ? "text-green-600" : "text-gray-900"}`}>
                      {r.progressPercent}%
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">{currency.format(r.remaining)}</td>
                    <td className={`px-4 py-2 text-right ${r.onTrack ? "text-green-600" : "text-red-600"}`}>
                      {currency.format(r.projectedTotal)} {r.onTrack ? "✓ en camino" : "⚠ por debajo"}
                    </td>
                  </tr>
                ))}
                {salesGoals.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                      Ningún vendedor tiene una meta mensual configurada (se hace en Usuarios).
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {belowCostSales && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3 pt-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Ventas por debajo del costo</h2>
              <p className="text-sm text-gray-500">
                Ventas cuyo precio efectivo quedó por debajo del costo del producto en ese momento — a diferencia del resto de este reporte, esto SÍ es un dato histórico exacto (el costo queda congelado en cada línea), no una estimación. El cajero tuvo que confirmar cada una explícitamente antes de cobrarla.
              </p>
            </div>
            <ExportButtons endpoint="/reports/store-below-cost-sales" filename="ventas-bajo-costo" params={{ dateFrom, dateTo, branchId: branchId || undefined }} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SummaryCard label="Pérdida estimada del periodo" value={belowCostSales.totalLossEstimate} tone={belowCostSales.totalLossEstimate > 0 ? "red" : "gray"} />
            <SummaryCard label="Líneas vendidas por debajo del costo" value={belowCostSales.lineCount} isCount tone={belowCostSales.lineCount > 0 ? "red" : "gray"} />
          </div>
          {belowCostSales.rows.length > 0 && (
            <div className="card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Venta</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Producto</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Sucursal</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Cant.</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Precio efectivo</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Costo</th>
                    <th className="px-4 py-2 text-right font-medium text-gray-500">Pérdida</th>
                    <th className="px-4 py-2 text-left font-medium text-gray-500">Cajero</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {belowCostSales.rows.map((r) => (
                    <tr key={r.saleLineId}>
                      <td className="px-4 py-2 text-gray-600">
                        #{r.saleNumber} <span className="text-xs text-gray-400">({r.saleDate})</span>
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        {r.productName} <span className="text-gray-400">({r.sku})</span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{r.branchName}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{r.quantity}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{currency.format(r.effectiveUnitPrice)}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{currency.format(r.unitCost)}</td>
                      <td className="px-4 py-2 text-right font-medium text-red-600">{currency.format(r.lossTotal)}</td>
                      <td className="px-4 py-2 text-gray-600">{r.createdByName ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {belowCostSales.rows.length === 0 && <p className="text-sm text-gray-400">Ninguna venta de este rango se hizo por debajo del costo.</p>}
        </>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3 pt-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Predicción de quiebre de stock (próximos 14 días)</h2>
          <p className="text-sm text-gray-500">
            Basado en el promedio de ventas de los últimos 30 días. "Sugerido a pedir" asume 7 días de tiempo de entrega del proveedor. Es una estimación simple (no considera estacionalidad) — úsala como alerta temprana, no como cifra exacta. Marca los productos que quieras pedir y genera órdenes de compra en borrador de un clic.
          </p>
        </div>
        <ExportButtons endpoint="/reports/store-stockout-forecast" filename="prediccion-quiebre-stock" params={{ daysAhead: 14 }} />
      </div>

      {!isLoadingForecast && stockoutForecast && stockoutForecast.length > 0 && (
        <div className="card flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Sucursal que recibe:</span>
            <select className="input-field w-auto" value={orderBranchId} onChange={(e) => setOrderBranchId(e.target.value)}>
              {branches?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={selectedProductIds.size === 0 || createDraftPurchases.isPending}
            onClick={handleGenerateDraftPurchases}
          >
            {createDraftPurchases.isPending ? "Generando..." : `Generar órdenes de compra (${selectedProductIds.size})`}
          </button>
        </div>
      )}

      {forecastResult && (
        <div className="card space-y-2 p-4 text-sm">
          {forecastResult.createdPurchases.length > 0 && (
            <div>
              <p className="font-medium text-green-700">
                {forecastResult.createdPurchases.length} orden(es) de compra creada(s) como borrador (revísalas y recíbelas en Compras):
              </p>
              <ul className="mt-1 list-inside list-disc text-gray-600">
                {forecastResult.createdPurchases.map((p) => (
                  <li key={p.id}>
                    {p.supplier?.name ?? "Proveedor"} — {p.lines.length} producto(s), total estimado {currency.format(Number(p.total))}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {forecastResult.skippedProductIds.length > 0 && (
            <p className="text-amber-700">
              {forecastResult.skippedProductIds.length} producto(s) no se incluyeron: no tienen ninguna compra previa registrada, así que no hay proveedor del cual inferir la orden. Agrégalos manualmente en Compras indicando el proveedor.
            </p>
          )}
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">
                {stockoutForecast && stockoutForecast.length > 0 && (
                  <input
                    type="checkbox"
                    checked={selectedProductIds.size > 0 && selectedProductIds.size === stockoutForecast.length}
                    onChange={toggleSelectAllForecast}
                    aria-label="Seleccionar todos"
                  />
                )}
              </th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Producto</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Stock actual</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Venta diaria prom.</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Días restantes</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Se agotaría el</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Cantidad a pedir</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoadingForecast && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Calculando...
                </td>
              </tr>
            )}
            {!isLoadingForecast &&
              stockoutForecast?.map((item) => {
                const isSelected = selectedProductIds.has(item.productId);
                return (
                  <tr key={item.productId} className={isSelected ? "bg-brand-50/40" : undefined}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={isSelected} onChange={() => toggleProductSelection(item.productId, item.suggestedOrderQty)} />
                    </td>
                    <td className="px-4 py-2 text-gray-900">
                      {item.productName} <span className="text-gray-400">({item.sku})</span>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">{item.currentStock}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{item.avgDailyDemand}</td>
                    <td className={`px-4 py-2 text-right font-medium ${item.daysRemaining <= 3 ? "text-red-600" : item.daysRemaining <= 7 ? "text-amber-600" : "text-gray-700"}`}>
                      {item.daysRemaining} {item.daysRemaining === 1 ? "día" : "días"}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">{item.estimatedStockoutDate}</td>
                    <td className="px-4 py-2 text-right">
                      <input
                        className="input-field ml-auto w-24 text-right"
                        type="number"
                        min="0"
                        value={orderQuantities[item.productId] ?? item.suggestedOrderQty}
                        onChange={(e) => setOrderQuantities((q) => ({ ...q, [item.productId]: e.target.value }))}
                      />
                    </td>
                  </tr>
                );
              })}
            {!isLoadingForecast && stockoutForecast?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Ningún producto se agotaría en los próximos 14 días al ritmo de venta actual.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {inventoryValue && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3 pt-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Valor de inventario</h2>
              <p className="text-sm text-gray-500">Costo de todo lo que hay en stock (todas las sucursales sumadas) y productos por debajo de su stock mínimo.</p>
            </div>
            <ExportButtons endpoint="/reports/store-inventory-value" filename="valor-inventario" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <SummaryCard label="Valor total (costo)" value={inventoryValue.totalValue} tone="brand" />
            <SummaryCard label="Unidades en stock" value={inventoryValue.totalUnits} isCount />
            <SummaryCard label="Productos en stock bajo" value={inventoryValue.lowStockCount} isCount tone={inventoryValue.lowStockCount > 0 ? "red" : "gray"} />
          </div>
          <div className="card overflow-hidden">
            <p className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">Productos con stock bajo mínimo</p>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Producto</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Stock actual</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Stock mínimo</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {inventoryValue.rows
                  .filter((r) => r.isLowStock)
                  .map((r) => (
                    <tr key={r.productId}>
                      <td className="px-4 py-2 text-gray-700">
                        {r.name} <span className="ml-1 text-xs text-gray-400">{r.sku}</span>
                      </td>
                      <td className="px-4 py-2 text-right text-red-600">{r.stockQty}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{r.minStock}</td>
                      <td className="px-4 py-2 text-right text-gray-900">{currency.format(r.value)}</td>
                    </tr>
                  ))}
                {inventoryValue.rows.filter((r) => r.isLowStock).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                      Ningún producto está por debajo de su stock mínimo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SalesByDayChart({ data }: { data: Array<{ day: string; total: number; count: number }> }) {
  if (data.length === 0) {
    return <p className="text-sm text-gray-400">Aún no hay ventas registradas en este rango.</p>;
  }
  const max = Math.max(...data.map((d) => d.total), 1);
  return (
    <div className="flex items-end gap-1 overflow-x-auto pb-1" style={{ minHeight: 160 }}>
      {data.map((d) => (
        <div key={d.day} className="flex flex-1 flex-col items-center gap-1" style={{ minWidth: 24 }} title={`${d.day}: ${currency.format(d.total)} (${d.count} venta(s))`}>
          <div className="w-full rounded-t bg-brand-500" style={{ height: `${Math.max(4, Math.round((d.total / max) * 130))}px` }} />
          <span className="w-full truncate text-center text-[10px] text-gray-400">{d.day.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

function ProductTable({ title, rows }: { title: string; rows: Array<{ productId: string; name: string; sku: string; quantitySold: number; revenue: number; profitEstimate: number }> }) {
  return (
    <div className="card overflow-hidden">
      <p className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">{title}</p>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-500">Producto</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Unidades</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Ingresos</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Utilidad*</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.productId}>
              <td className="px-4 py-2 text-gray-700">
                {r.name}
                <span className="ml-1 text-xs text-gray-400">{r.sku}</span>
              </td>
              <td className="px-4 py-2 text-right text-gray-600">{r.quantitySold}</td>
              <td className="px-4 py-2 text-right text-gray-900">{currency.format(r.revenue)}</td>
              <td className={`px-4 py-2 text-right ${r.profitEstimate >= 0 ? "text-green-700" : "text-red-600"}`}>{currency.format(r.profitEstimate)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                Sin ventas en este rango.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RotationTable({ title, rows }: { title: string; rows: Array<{ productId: string; name: string; sku: string; quantitySold: number }> }) {
  return (
    <div className="card overflow-hidden">
      <p className="border-b border-gray-100 p-4 text-sm font-semibold text-gray-900">{title}</p>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-500">Producto</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">Unidades vendidas</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.productId}>
              <td className="px-4 py-2 text-gray-700">
                {r.name}
                <span className="ml-1 text-xs text-gray-400">{r.sku}</span>
              </td>
              <td className="px-4 py-2 text-right font-medium text-gray-900">{r.quantitySold}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={2} className="px-4 py-6 text-center text-gray-400">
                Sin ventas en este rango.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SummaryCard({ label, value, tone = "gray", isCount = false, suffix }: { label: string; value: number; tone?: "gray" | "red" | "brand" | "green"; isCount?: boolean; suffix?: string }) {
  const toneStyle: Record<string, string> = { gray: "text-gray-900", red: "text-red-600", brand: "text-brand-600", green: "text-green-700" };
  return (
    <div className="card p-4">
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${toneStyle[tone]}`}>{isCount || suffix ? `${value}${suffix ?? ""}` : currency.format(value)}</p>
    </div>
  );
}
